// Network sessions: a network admin signs in with their own (home) login and switches into
// the owner account of each pro group in their network. The switched session is a bare
// access token (no refresh token, up to 12h). While switched, the home tokens wait under
// separate keys so "Back to network" — or an expired owner session — can restore them.
import { authApi } from "src/api/auth";
import { paths } from "src/paths";

const STORAGE_KEY = "s_at";
const REFRESH_STORAGE_KEY = "s_rt";
export const NETWORK_HOME_AT_KEY = "network_home_s_at";
export const NETWORK_HOME_RT_KEY = "network_home_s_rt";
// Survives the full-page navigation to the landing page so it can explain why they're there.
const NETWORK_EXPIRED_NOTICE_KEY = "network_session_expired";

export const NETWORK_LANDING_PATH = paths.network;

export interface NetworkAccount {
  role_id: string;
  pro_group_id: string;
  pro_group_name: string;
  network_id: string;
  network_name: string;
  owner_ud_id: string;
  owner_email: string;
  logo: string | null;
}

export interface NetworkAccountsResponse {
  // True for a network admin even before Snug has set up any pro groups for them
  is_network_admin: boolean;
  accounts: NetworkAccount[];
  switched: boolean;
  home_email?: string;
  current_ud_id?: string;
}

export interface NetworkSwitchResponse {
  access: string;
  pro_group_id: string;
  owner_ud_id: string;
}

const EMPTY_ACCOUNTS: NetworkAccountsResponse = { is_network_admin: false, accounts: [], switched: false };

// True while an owner account is the active session.
export const isNetworkSwitched = (): boolean =>
  !!localStorage.getItem(NETWORK_HOME_AT_KEY);

// Make an owner account's access token the session. The first switch saves the home tokens;
// switching between owner accounts keeps them (never overwrite them with an owner token).
export const startNetworkSession = (access: string): void => {
  if (!isNetworkSwitched()) {
    const homeAt = localStorage.getItem(STORAGE_KEY);
    const homeRt = localStorage.getItem(REFRESH_STORAGE_KEY);
    if (homeAt) localStorage.setItem(NETWORK_HOME_AT_KEY, homeAt);
    if (homeRt) localStorage.setItem(NETWORK_HOME_RT_KEY, homeRt);
  }
  localStorage.setItem(STORAGE_KEY, access);
  // No refresh token for owner sessions: never refresh them with the home refresh token.
  localStorage.removeItem(REFRESH_STORAGE_KEY);
  resetNetworkAccountsCache();
};

// Put the home tokens back as the session. The home access token may have aged out while
// they were switched, so try the home refresh token first and fall back to what was saved.
export const restoreNetworkHome = async (): Promise<boolean> => {
  const homeAt = localStorage.getItem(NETWORK_HOME_AT_KEY);
  const homeRt = localStorage.getItem(NETWORK_HOME_RT_KEY);
  if (!homeAt) return false;
  let access = homeAt;
  let refresh = homeRt;
  if (homeRt) {
    try {
      const res = await authApi.refresh({ refresh: homeRt });
      access = res.s_at;
      refresh = res.s_rt || homeRt;
    } catch (err) {
      console.error("[network-session] home refresh failed, restoring saved token", err);
    }
  }
  localStorage.setItem(STORAGE_KEY, access);
  if (refresh) {
    localStorage.setItem(REFRESH_STORAGE_KEY, refresh);
  } else {
    localStorage.removeItem(REFRESH_STORAGE_KEY);
  }
  clearNetworkHome();
  return true;
};

export const clearNetworkHome = (): void => {
  localStorage.removeItem(NETWORK_HOME_AT_KEY);
  localStorage.removeItem(NETWORK_HOME_RT_KEY);
  resetNetworkAccountsCache();
};

// Full navigation so every context reloads as the new session's user.
export const goToNetworkLanding = (): void => {
  window.location.replace(NETWORK_LANDING_PATH);
};

let expiryInProgress = false;

// The owner session expired or was rejected. If home tokens are waiting, restore them and
// send the user to the landing page instead of signing out. Returns true when handled; many
// requests can 401 at once, so later calls just report it as handled.
export const handleNetworkSessionExpired = async (): Promise<boolean> => {
  if (expiryInProgress) return true;
  if (!isNetworkSwitched()) return false;
  expiryInProgress = true;
  await restoreNetworkHome();
  sessionStorage.setItem(NETWORK_EXPIRED_NOTICE_KEY, "true");
  goToNetworkLanding();
  return true;
};

// Read once by the landing page.
export const takeNetworkExpiredNotice = (): boolean => {
  const notice = sessionStorage.getItem(NETWORK_EXPIRED_NOTICE_KEY) === "true";
  sessionStorage.removeItem(NETWORK_EXPIRED_NOTICE_KEY);
  return notice;
};

// Accounts are fetched once per page load (switching and going back both reload the page).
let accountsPromise: Promise<NetworkAccountsResponse> | null = null;

export const loadNetworkAccounts = (
  fetcher: () => Promise<NetworkAccountsResponse | undefined>
): Promise<NetworkAccountsResponse> => {
  if (!accountsPromise) {
    accountsPromise = fetcher()
      .then((data) => ({
        ...EMPTY_ACCOUNTS,
        ...data,
        accounts: data?.accounts || [],
        // Older API responses had no flag: having owner accounts meant being a network admin
        is_network_admin: Boolean(data?.is_network_admin || data?.accounts?.length),
      }))
      .catch((err) => {
        // Let the next caller try again
        accountsPromise = null;
        throw err;
      });
  }
  return accountsPromise;
};

export const resetNetworkAccountsCache = (): void => {
  accountsPromise = null;
};

export interface NetworkAccountGroup {
  network_id: string;
  network_name: string;
  accounts: NetworkAccount[];
}

// Accounts grouped by network, keeping the API's order.
export const groupAccountsByNetwork = (
  accounts: NetworkAccount[]
): NetworkAccountGroup[] => {
  const groups: NetworkAccountGroup[] = [];
  accounts.forEach((account) => {
    let group = groups.find((g) => g.network_id === account.network_id);
    if (!group) {
      group = {
        network_id: account.network_id,
        network_name: account.network_name,
        accounts: [],
      };
      groups.push(group);
    }
    group.accounts.push(account);
  });
  return groups;
};
